import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, MessageSquare, TrendingUp, Star, CheckCircle,
  Clock, AlertCircle, XCircle, Eye, ChevronRight,
  BarChart2, Globe, Video, Activity, LogOut, RefreshCw,
  UserCheck, Mail, Phone, MapPin, Calendar, Trophy,
  Filter, Search, Dumbbell, ClipboardList, Target,
} from 'lucide-react';
import TrainingPlansTab from './TrainingPlansTab';
import TrainingSessionsTab from './TrainingSessionsTab';
import TestsGuideTab from './TestsGuideTab';
import PlayerProfile from './PlayerProfile';

// ─── API helpers ──────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || '';

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('prokids_token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, delta }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {delta !== undefined && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${delta >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
            {delta >= 0 ? '+' : ''}{delta}%
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-1">{value ?? '–'}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const config = {
    PENDING:      { icon: Clock,        color: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Pendiente' },
    UNDER_REVIEW: { icon: Eye,          color: 'bg-blue-50 text-blue-700 border-blue-200',       label: 'En revisión' },
    PUBLISHED:    { icon: CheckCircle,  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Publicado' },
    SUSPENDED:    { icon: XCircle,      color: 'bg-red-50 text-red-700 border-red-200',           label: 'Suspendido' },
    NEW:          { icon: AlertCircle,  color: 'bg-amber-50 text-amber-700 border-amber-200',     label: 'Nuevo' },
    CONTACTED:    { icon: CheckCircle,  color: 'bg-blue-50 text-blue-700 border-blue-200',        label: 'Contactado' },
    ENROLLED:     { icon: Trophy,       color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Inscrito' },
    LOST:         { icon: XCircle,      color: 'bg-gray-50 text-gray-500 border-gray-200',        label: 'Perdido' },
  };

  const { icon: Icon, color, label } = config[status] ?? config['PENDING'];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}

// ─── Players Tab ──────────────────────────────────────────────────────────────
function PlayersTab() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [updating, setUpdating] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const data = await apiFetch(`/api/v1/admin/players?${params}`);
      setPlayers(data.players ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await apiFetch(`/api/v1/admin/players/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setPlayers(prev => prev.map(p => p.id === id ? { ...p, status } : p));
    } catch (e) {
      alert('Error actualizando estado');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = players.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.country?.toLowerCase().includes(search.toLowerCase())
  );

  if (selectedPlayerId) {
    return <PlayerProfile playerId={selectedPlayerId} onBack={() => setSelectedPlayerId(null)} />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-48 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar jugador o país…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Todos los estados</option>
          <option value="PENDING">Pendientes</option>
          <option value="UNDER_REVIEW">En revisión</option>
          <option value="PUBLISHED">Publicados</option>
          <option value="SUSPENDED">Suspendidos</option>
        </select>
        <button
          onClick={loadPlayers}
          className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Cargando jugadores…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No hay jugadores que coincidan</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Jugador</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">País</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Posición</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Estado</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(player => (
                <tr key={player.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedPlayerId(player.id)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                        {player.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{player.name}</div>
                        <div className="text-xs text-gray-400">{player.age} años · /{player.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <Globe className="w-3.5 h-3.5 text-gray-400" />
                      {player.country}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-600">{player.position}</td>
                  <td className="px-4 py-3"><StatusBadge status={player.status} /></td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {player.status === 'PENDING' && (
                        <button
                          onClick={() => updateStatus(player.id, 'UNDER_REVIEW')}
                          disabled={updating === player.id}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition disabled:opacity-50"
                        >
                          Revisar
                        </button>
                      )}
                      {(player.status === 'PENDING' || player.status === 'UNDER_REVIEW') && (
                        <button
                          onClick={() => updateStatus(player.id, 'PUBLISHED')}
                          disabled={updating === player.id}
                          className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition disabled:opacity-50"
                        >
                          Publicar
                        </button>
                      )}
                      {player.status === 'PUBLISHED' && (
                        <button
                          onClick={() => updateStatus(player.id, 'SUSPENDED')}
                          disabled={updating === player.id}
                          className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 transition disabled:opacity-50"
                        >
                          Suspender
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            {filtered.length} jugador{filtered.length !== 1 ? 'es' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contacts Tab ─────────────────────────────────────────────────────────────
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [updated, setUpdated] = useState(null);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/api/v1/admin/contacts${statusFilter ? `?status=${statusFilter}` : ''}`);
      setContacts(data.contacts ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const updateStatus = async (id, status) => {
    setUpdated(id);
    try {
      await apiFetch(`/api/v1/admin/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setContacts(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    } catch (e) {
      alert('Error actualizando lead');
    } finally {
      setUpdated(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Todos</option>
          <option value="NEW">Nuevos</option>
          <option value="CONTACTED">Contactados</option>
          <option value="ENROLLED">Inscritos</option>
          <option value="LOST">Perdidos</option>
        </select>
        <button
          onClick={loadContacts}
          className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Cargando leads…
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No hay leads con este filtro</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map(contact => (
            <div
              key={contact.id}
              className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition"
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900">{contact.name}</span>
                    <StatusBadge status={contact.status} />
                    {contact.inquiryType && contact.inquiryType !== 'GENERAL' && (
                      <span className="text-xs text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">
                        {contact.inquiryType}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-emerald-600 transition">
                      <Mail className="w-3.5 h-3.5" />
                      {contact.email}
                    </a>
                    <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-emerald-600 transition">
                      <Phone className="w-3.5 h-3.5" />
                      {contact.phone}
                    </a>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {contact.city}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(contact.createdAt).toLocaleDateString('es-LATAM')}
                    </span>
                  </div>
                  {contact.playerName && (
                    <div className="text-sm text-gray-600">
                      🧒 <strong>{contact.playerName}</strong>{contact.playerAge ? `, ${contact.playerAge} años` : ''}
                    </div>
                  )}
                  {contact.message && (
                    <p className="text-sm text-gray-500 italic bg-gray-50 rounded-lg p-3 mt-2">
                      "{contact.message}"
                    </p>
                  )}
                </div>

                {/* Acciones rápidas */}
                <div className="flex gap-2 flex-shrink-0 flex-wrap">
                  <a
                    href={`https://wa.me/${contact.phone?.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(contact.name)}%2C%20soy%20del%20equipo%20de%20ProKids%20Talent%2C%20te%20contacto%20por%20tu%20solicitud.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition"
                  >
                    WhatsApp
                  </a>
                  {contact.status === 'NEW' && (
                    <button
                      onClick={() => updateStatus(contact.id, 'CONTACTED')}
                      disabled={updated === contact.id}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition disabled:opacity-50"
                    >
                      Marcar contactado
                    </button>
                  )}
                  {contact.status === 'CONTACTED' && (
                    <button
                      onClick={() => updateStatus(contact.id, 'ENROLLED')}
                      disabled={updated === contact.id}
                      className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition disabled:opacity-50"
                    >
                      ✓ Inscrito
                    </button>
                  )}
                  {!['ENROLLED', 'LOST'].includes(contact.status) && (
                    <button
                      onClick={() => updateStatus(contact.id, 'LOST')}
                      disabled={updated === contact.id}
                      className="px-3 py-1.5 bg-gray-50 text-gray-500 rounded-lg text-xs font-medium hover:bg-gray-100 transition disabled:opacity-50"
                    >
                      Perdido
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Metrics Tab ──────────────────────────────────────────────────────────────
function MetricsTab() {
  const [metrics, setMetrics] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch('/api/v1/public/metrics')
      .then(data => {
        setMetrics(data.metrics);
        setForm(data.metrics ?? {});
      })
      .catch(console.error);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/v1/admin/site-metrics', {
        method: 'PATCH',
        body: JSON.stringify({
          activeKids: parseInt(form.activeKids),
          countries: parseInt(form.countries),
          videosCreated: parseInt(form.videosCreated),
          connectedScouts: parseInt(form.connectedScouts),
        }),
      });
      setMetrics(form);
      setEditing(false);
    } catch (e) {
      alert('Error guardando métricas');
    } finally {
      setSaving(false);
    }
  };

  if (!metrics) {
    return (
      <div className="text-center py-12 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        Cargando métricas…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-700">Métricas de la Landing Page</h3>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-sm font-medium hover:bg-emerald-100 transition"
          >
            Editar métricas
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'activeKids', label: 'Niños activos', icon: Users, color: 'emerald' },
          { key: 'countries', label: 'Países', icon: Globe, color: 'blue' },
          { key: 'videosCreated', label: 'Videos creados', icon: Video, color: 'purple' },
          { key: 'connectedScouts', label: 'Scouts conectados', icon: UserCheck, color: 'yellow' },
        ].map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <Icon className="w-5 h-5 text-gray-400" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
            </div>
            {editing ? (
              <input
                type="number"
                value={form[key] ?? ''}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full text-2xl font-bold text-gray-900 border-b-2 border-emerald-400 focus:outline-none bg-transparent"
              />
            ) : (
              <div className="text-3xl font-bold text-gray-900">{metrics[key]?.toLocaleString()}</div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
        <strong>⚠️ Nota:</strong> Estos valores se muestran en tiempo real en la landing page (hero section). Los cambios son visibles inmediatamente.
      </div>
    </div>
  );
}

// ─── Scout Interests Tab ──────────────────────────────────────────────────────
function ScoutsTab() {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/v1/scouts/admin/interests')
      .then(data => setInterests(data.interests ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await apiFetch(`/api/v1/scouts/admin/interests/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setInterests(prev => prev.map(i => i.id === id ? { ...i, status } : i));
    } catch (e) {
      alert('Error actualizando');
    }
  };

  const verifyScout = async (scoutId) => {
    try {
      await apiFetch(`/api/v1/scouts/admin/scouts/${scoutId}/verify`, { method: 'PATCH' });
      setInterests(prev => prev.map(i => i.scoutId === scoutId ? { ...i, scout: { ...i.scout, isVerified: true } } : i));
    } catch (e) {
      alert('Error verificando scout');
    }
  };

  if (loading) return (
    <div className="text-center py-12 text-gray-400">
      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
      Cargando intereses de scouts…
    </div>
  );

  return (
    <div className="space-y-3">
      {interests.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Star className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Aún no hay intereses de scouts registrados</p>
        </div>
      ) : interests.map(interest => (
        <div key={interest.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{interest.scout?.name}</span>
                {interest.scout?.isVerified ? (
                  <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">✓ Verificado</span>
                ) : (
                  <button
                    onClick={() => verifyScout(interest.scoutId)}
                    className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full hover:bg-orange-100 transition"
                  >
                    Verificar scout
                  </button>
                )}
              </div>
              <div className="text-sm text-gray-500">{interest.scout?.organization} · {interest.scout?.email}</div>
              <div className="text-sm font-medium text-gray-700 mt-1">
                👟 Interesado en: <strong>{interest.player?.name}</strong>
                {` · ${interest.player?.position} · ${interest.player?.age} años`}
              </div>
              {interest.message && (
                <p className="text-sm text-gray-500 italic bg-gray-50 rounded-lg p-2 mt-1">"{interest.message}"</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={interest.status} />
              {interest.status === 'PENDING' && (
                <button
                  onClick={() => updateStatus(interest.id, 'CONTACTED')}
                  className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition"
                >
                  Marcar contactado
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main AdminDashboard Component ───────────────────────────────────────────
export default function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      apiFetch('/api/v1/public/metrics'),
      apiFetch('/api/v1/admin/players?limit=5'),
      apiFetch('/api/v1/admin/contacts?status=NEW'),
      apiFetch('/api/v1/scouts/admin/interests'),
    ]).then(([metrics, players, contacts, scouts]) => {
      setOverview({
        metrics: metrics.value?.metrics,
        totalPlayers: players.value?.total ?? 0,
        pendingPlayers: players.value?.players?.filter(p => p.status === 'PENDING')?.length ?? 0,
        newLeads: contacts.value?.total ?? 0,
        scoutInterests: scouts.value?.total ?? 0,
      });
    });
  }, []);

  const tabs = [
    { id: 'overview',  label: 'Resumen',  icon: BarChart2 },
    { id: 'players',   label: 'Jugadores', icon: Users },
    { id: 'contacts',  label: 'Leads CRM', icon: MessageSquare },
    { id: 'scouts',    label: 'Scouts',   icon: Star },
    { id: 'metrics',   label: 'Métricas', icon: Activity },
    { id: 'training',  label: 'Entrenamientos', icon: Dumbbell },
    { id: 'sessions',  label: 'Sesiones', icon: ClipboardList },
    { id: 'tests',     label: 'Tests', icon: Target },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Trophy className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-gray-900">ProKids</span>
              <span className="font-light text-emerald-600"> Admin</span>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-500 transition"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Cerrar sesión</span>
          </button>
        </div>

        {/* Tab navigation */}
        <div className="max-w-7xl mx-auto flex gap-1 overflow-x-auto pb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <h2 className="text-xl font-bold text-gray-900">Panel de Control</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users}       label="Jugadores totales"    value={overview?.totalPlayers}     color="emerald" />
              <StatCard icon={Clock}       label="Jugadores pendientes" value={overview?.pendingPlayers}   color="yellow" />
              <StatCard icon={MessageSquare} label="Leads nuevos"      value={overview?.newLeads}          color="blue" />
              <StatCard icon={Star}        label="Intereses scouts"     value={overview?.scoutInterests}   color="purple" />
            </div>

            {overview?.metrics && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-4">Métricas de la Landing</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={Users}    label="Niños activos"    value={`${overview.metrics.activeKids}+`}    color="emerald" />
                  <StatCard icon={Globe}    label="Países"           value={overview.metrics.countries}             color="blue" />
                  <StatCard icon={Video}    label="Videos creados"   value={`${overview.metrics.videosCreated}+`}  color="purple" />
                  <StatCard icon={UserCheck} label="Scouts conectados" value={overview.metrics.connectedScouts}   color="yellow" />
                </div>
              </div>
            )}

            {/* Quick links */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { tab: 'players',  label: 'Revisar jugadores pendientes', icon: Users,        color: 'emerald' },
                { tab: 'contacts', label: 'Gestionar leads nuevos',        icon: MessageSquare, color: 'blue' },
                { tab: 'scouts',   label: 'Ver intereses de scouts',       icon: Star,         color: 'purple' },
              ].map(link => (
                <button
                  key={link.tab}
                  onClick={() => setActiveTab(link.tab)}
                  className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition group"
                >
                  <div className="flex items-center gap-3">
                    <link.icon className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition" />
                    <span className="text-sm font-medium text-gray-700">{link.label}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-600 transition" />
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'players'  && <PlayersTab />}
        {activeTab === 'contacts' && <ContactsTab />}
        {activeTab === 'scouts'   && <ScoutsTab />}
        {activeTab === 'metrics'  && <MetricsTab />}
        {activeTab === 'training' && <TrainingPlansTab />}
        {activeTab === 'sessions' && <TrainingSessionsTab />}
        {activeTab === 'tests' && <TestsGuideTab />}
      </div>
    </div>
  );
}
